import type { FeaturedVideoProperty } from '~/core/entities/property';
import type { ComputedRef } from 'vue';

import { floatToLocaleString } from '#imports';
import { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import { getAreaLabel } from '~/core/entities/SystemEnums';
import { grammarInflection } from '~~/utils';

export function usePropertyInfo(
	property: ComputedRef<FeaturedVideoProperty | Readonly<FeaturedVideoProperty>>
) {
	const formattedPrice = computed(() => {
		const price = property.value.askingPrice;
		if (!price) return null;
		return floatToLocaleString(price, { maximumFractionDigits: 0 });
	});

	const formattedArea = computed(() => {
		if (!property.value.area) return null;
		return new Intl.NumberFormat('pt-BR', {
			maximumFractionDigits: 0
		}).format(Math.round(property.value.area));
	});

	const areaLabel = computed(() => {
		if (!property.value.propertyType) return 'útil';
		return getAreaLabel(
			property.value.propertyType.identifier as PropertyTypeEnum
		).toLowerCase();
	});

	const roomsInfo = computed(() => {
		const { suites, bedrooms } = property.value;
		if (suites && suites > 0)
			return {
				count: suites,
				label: grammarInflection(suites, 'suíte', 'suítes')
			};
		if (bedrooms && bedrooms > 0)
			return {
				count: bedrooms,
				label: grammarInflection(bedrooms, 'quarto', 'quartos')
			};
		return null;
	});

	const location = computed(() => {
		const { propertyType, region, city, state } = property.value;
		const typeName = propertyType?.name;
		const addressParts = [region, city].filter(Boolean).join(', ');
		const fullLocation = state ? `${addressParts} - ${state}` : addressParts;
		if (typeName && fullLocation) return `${typeName} em ${fullLocation}`;
		return fullLocation || typeName || '';
	});

	return {
		formattedPrice,
		formattedArea,
		areaLabel,
		roomsInfo,
		location
	};
}
